import { useNavigate } from 'react-router-dom';
import type { SearchRadioStation } from '@/mocks/searchRadios';
import { useAudioPlayer } from '@/context/AudioContext';
import { radioStations } from '@/mocks/radios';

interface RadioResultCardProps {
  radio: SearchRadioStation;
  query?: string;
}

function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query || query.length < 2) return text;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part)
      ? <span key={i} className="text-primary-600 font-semibold">{part}</span>
      : part
  );
}

export default function RadioResultCard({ radio, query = '' }: RadioResultCardProps) {
  const navigate = useNavigate();
  const { play, isPlaying, currentStation } = useAudioPlayer();

  const radioPath = `/radio/${radio.frequency.replace('.', '-')}`;
  const isThisPlaying = isPlaying && currentStation && currentStation.frequency === radio.frequency;

  const handlePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    const station = radioStations.find((s) => s.frequency === radio.frequency);
    if (station) {
      play(station);
    }
  };

  return (
    <div
      onClick={() => navigate(radioPath)}
      className="group relative bg-background-50 rounded-xl p-4 md:p-5 border border-background-200/70 hover:border-primary-200 hover:bg-background-50 transition-all duration-200 cursor-pointer flex flex-col gap-3"
    >
      {radio.isLive && (
        <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/20">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
          <span className="text-[10px] font-semibold text-red-600 uppercase tracking-wider">En vivo</span>
        </div>
      )}

      <div className="flex items-start gap-3">
        <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl overflow-hidden shrink-0 bg-background-100">
          <img
            src={radio.logoUrl}
            alt={radio.name}
            className="w-full h-full object-contain"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm md:text-base font-semibold text-foreground-950 truncate pr-16">
            {highlightMatch(radio.name, query)}
          </h3>
          <div className="flex items-center gap-1.5 mt-0.5 text-xs text-foreground-500">
            <i className="ri-map-pin-line text-[10px]"></i>
            <span>
              {highlightMatch(`${radio.city}${radio.state ? `, ${radio.state}` : ''}, ${radio.country}`, query)}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-800 text-[10px] font-semibold whitespace-nowrap">
          <i className="ri-radio-line text-xs"></i>
          {highlightMatch(`${radio.frequency}`, query)}
        </span>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-background-100 text-foreground-600 text-[10px] font-medium whitespace-nowrap">
          <i className="ri-price-tag-3-line text-xs"></i>
          {highlightMatch(radio.category, query)}
        </span>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-background-100 text-foreground-600 text-[10px] font-medium whitespace-nowrap">
          <i className="ri-global-line text-xs"></i>
          {highlightMatch(radio.language, query)}
        </span>
      </div>

      <div className="flex flex-wrap gap-1">
        {radio.announcers.map((announcer) => (
          <span
            key={announcer}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-background-100/80 text-foreground-600 text-[10px] font-medium whitespace-nowrap"
          >
            <i className="ri-mic-line text-xs"></i>
            {highlightMatch(announcer, query)}
          </span>
        ))}
      </div>

      <div className="flex items-center justify-between pt-1 border-t border-background-200/50">
        <div className="flex items-center gap-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-headphone-line text-sm"></i>
            <span className="font-medium text-foreground-700">{radio.listeners.toLocaleString()}</span>
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-star-line text-sm text-amber-500"></i>
            <span className="font-medium text-foreground-700">{radio.rating}</span>
          </span>
          <span className="hidden sm:flex items-center gap-1">
            <i className="ri-message-3-line text-sm"></i>
            <span className="font-medium text-foreground-700">{radio.cabinaMessages.toLocaleString()}</span>
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={handlePlay}
            className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-all ${
              isThisPlaying
                ? 'bg-accent-500 text-background-50'
                : 'bg-primary-500 text-background-50 hover:bg-primary-600'
            }`}
            title={isThisPlaying ? 'Pausar' : 'Escuchar'}
          >
            <i className={`text-xs ${isThisPlaying ? 'ri-pause-fill' : 'ri-play-fill'}`}></i>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); navigate(radioPath); }}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-right-line"></i>
            Entrar
          </button>
        </div>
      </div>
    </div>
  );
}